import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import logo from "../../assets/favicon.png";
import { useCart } from "../../contexts/CartContext";
import { useSearch } from "../../contexts/SearchContext";
import { useContactSettings } from "../../hooks/useContactSettings";
import { useStats } from "../../hooks/useHomepageQueries";
import api from "../../services/api";
import SearchBarWithQuery from "../ui/SearchBarWithQuery";
import MobileSearchBarWithQuery from "../ui/MobileSearchBarWithQuery";
import BulkOrderUpload from "../home/BulkOrderUpload";
import { Button } from "../ui/button";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import * as Collapsible from "@radix-ui/react-collapsible";
import * as Separator from "@radix-ui/react-separator";
import * as Tooltip from "@radix-ui/react-tooltip";
import * as Avatar from "@radix-ui/react-avatar";
import {
  MdMenu,
  MdPerson,
  MdFavorite,
  MdClose,
  MdLogout,
  MdShoppingCart,
  MdKeyboardArrowRight,
  MdKeyboardArrowLeft,
  MdGridView,
  MdLogin,
  MdDashboard,
  MdStore,
  MdStars,
  MdTimer,
  MdLocalOffer,
  MdSell,
  MdTrendingUp,
  MdLocalHospital,
  MdCardMembership,
  MdInfo,
  MdHourglassEmpty,
  MdArticle
} from "react-icons/md";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { Mail, Phone, Package, Award, CheckCircle2, ShieldCheck, User, MapPin, ChevronDown, Heart, ShoppingCart, PackagePlus } from "lucide-react";
import { MdCloudUpload } from "react-icons/md";

// Add this helper function at the top of the component
const renderCategoryWithIndentation = (category, level = 0) => {
  return (
    <React.Fragment key={category._id}>
      <Link
        to={`/category/${category.slug}`}
        className={`block px-4 py-2 hover:bg-primary-100 text-sm ${level > 0 ? `pl-${4 + level * 4}` : ""
          }`}
      >
        {level > 0 && "└─ "}
        {category.name}
      </Link>
      {category.children?.map((child) =>
        renderCategoryWithIndentation(child, level + 1)
      )}
    </React.Fragment>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBulkOrderOpen, setIsBulkOrderOpen] = useState(false);

  // Removed "vibecode" Quick Bulk Order CTA styling and replaced with cleaner Tailwind classes
  const [categories, setCategories] = useState([]);
  const [eventsCount, setEventsCount] = useState(0);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const { currentUser, logout } = useAuth();
  const { itemCount = 0, totalValue = 0 } = useCart();
  const { contactSettings } = useContactSettings();
  const { data: stats } = useStats();
  const navigate = useNavigate();
  const location = useLocation();

  const isAccountRoute = location.pathname.startsWith("/account");

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedLevelIndex, setSelectedLevelIndex] = useState(0);

  const dropdownTimeoutRef = useRef(null);

  const handleMouseEnterDropdown = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeaveDropdown = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 300);
  };

  const handleOpenChange = (open) => {
    // Only allow Radix to change state if opening. 
    // We handle closing via our own hover logic to prevent shivering.
    if (open) {
      setIsDropdownOpen(true);
    }
  };

  // Add custom CSS for hiding scrollbars
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .hide-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .blink { 
        animation: blink 1s step-end infinite;
      }
      @keyframes blink {
        0%, 49% { opacity: 1; }
        50%, 100% { opacity: 0.25; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get("/categories");

        if (response.data.data && Array.isArray(response.data.data)) {
          // Log the first category to inspect its structure
          if (response.data.data.length > 0) {
          }
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchEventsCount = async () => {
      try {
        const response = await api.get("/events");
        setEventsCount(response.data.count ?? (response.data.data?.length || 0));
      } catch (error) {
        console.error("Error fetching events count:", error);
      }
    };

    fetchEventsCount();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSelectedCategories([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search is now handled by SearchBar component

  const handleAccountClick = () => {
    if (!currentUser) {
      navigate("/login");
    } else {
      navigate("/account");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const handleWishlistClick = (e) => {
    if (!currentUser) {
      e.preventDefault();
      // Save the intended destination
      navigate("/login", { state: { from: { pathname: "/wishlist" } } });
    }
  };

  // Ripple effect removed for a cleaner aesthetic

  const handleCategoryClick = (category, level, e) => {
    // Navigate to the category page
    navigate(`/category/${category.slug}`);

    // Close dropdown after navigation
    setIsDropdownOpen(false);
    setSelectedCategories([]);
    setSelectedLevelIndex(0);
  };

  const handleExpandSubcategory = (category, level, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Update selected categories array up to the current level and add new selection
    setSelectedCategories((prev) => {
      const newSelected = [...prev.slice(0, level), category];
      return newSelected;
    });

    // Move to next level if category has children (for both mobile and desktop)
    if (category.children?.length > 0) {
      setSelectedLevelIndex(level + 1);
    }
  };

  const handleBackClick = () => {
    setSelectedLevelIndex((prev) => Math.max(0, prev - 1));
    setSelectedCategories((prev) => prev.slice(0, -1));
  };

  const getCategoriesForLevel = (level) => {
    if (level === 0) {
      return categories.filter(category => category.level === 0);
    }

    const parentCategory = selectedCategories[level - 1];
    return parentCategory?.children || [];
  };

  const getMaxLevel = () => {
    const findMaxLevel = (categories, level = 0) => {
      if (!categories || categories.length === 0) return level;

      let maxLevel = level;
      categories.forEach((category) => {
        if (category.children?.length > 0) {
          const childLevel = findMaxLevel(category.children, level + 1);
          maxLevel = Math.max(maxLevel, childLevel);
        }
      });
      return maxLevel;
    };

    return findMaxLevel(categories);
  };

  // Return array of numbers from 0 to max level found in categories
  const getLevels = () => {
    const maxLevel = getMaxLevel();
    return Array.from({ length: maxLevel + 1 }, (_, i) => i);
  };

  return (
    <header
      className={`bg-[#FFFFF0] text-gray-800 shadow-sm border-b border-gray-200 ${!isAccountRoute
        ? "sticky top-0 md:h-[92px]" // 56px for top bar + 36px for categories
        : "h-12 md:h-14"
        } z-header`}
    >
      {/* Top Navigation Bar */}
      <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 h-12 md:h-14">
        <div className="flex items-center justify-between gap-2 h-full">
          {/* Logo & Location */}
          <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
            {/* Plain <a> (not React Router's Link) so this always does a full
                page refresh, including when already on the homepage. */}
            <a href="/" className="flex items-center gap-2">
              <img
                src={logo}
                alt="CureHive Nepal"
                className="h-9 md:h-11 w-auto object-contain flex-shrink-0"
              />
              <span className="hidden sm:flex flex-col leading-none">
                <span className="text-lg md:text-xl font-heading font-bold text-[#1B365D]">CureHive</span>
                <span className="text-[10px] md:text-xs font-bold text-secondary-600 tracking-widest">NEPAL</span>
              </span>
            </a>

            {/* Location Widget - Desktop only */}
            <div className="hidden md:flex items-center bg-white px-3.5 py-2 rounded-full border border-gray-300 cursor-pointer hover:border-secondary-300 hover:shadow-sm transition-all">
              <MapPin className="w-4 h-4 text-secondary-600 mr-2 flex-shrink-0" strokeWidth={2} />
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 font-medium leading-tight">Delivering to</span>
                <span className="text-xs text-[#1B365D] font-bold leading-tight">Nepal</span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400 ml-1.5" strokeWidth={2} />
            </div>
          </div>

          {/* Search Bar - Visible on all devices */}
          <div className="flex flex-1 mx-2 md:mx-6 max-w-2xl">
            {/* Desktop SearchBar */}
            <div className="hidden md:flex w-full">
              <SearchBarWithQuery
                className="w-full"
              />
            </div>

            {/* Mobile SearchBar */}
            <div className="flex md:hidden w-full">
              <MobileSearchBarWithQuery />
            </div>
          </div>

          {/* Desktop Navigation Items */}
          <div className="hidden md:flex items-center gap-3">
            <Tooltip.Provider>
              {/* Account Avatar & Dropdown */}
              <Tooltip.Root>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button className="flex items-center gap-1.5 px-2 py-2 text-[#1B365D] hover:text-secondary-600 transition-colors bg-transparent border-none">
                      <User className="w-5 h-5" strokeWidth={2} />
                      <span className="text-sm font-semibold hidden md:block">
                        {currentUser ? currentUser.displayName?.split(' ')[0] || "Account" : "Login"}
                      </span>
                    </button>
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      className="min-w-[200px] bg-white rounded-lg shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] border border-gray-100 p-1 z-[9999]"
                      sideOffset={5}
                    >
                      {currentUser ? (
                        <>
                          <div className="px-3 py-2 border-b border-gray-100">
                            <p className="text-sm font-medium text-gray-900">
                              {currentUser.displayName || "User"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {currentUser.email}
                            </p>
                          </div>
                          <DropdownMenu.Item asChild>
                            <Link
                              to="/account"
                              className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md cursor-pointer"
                            >
                              <MdDashboard className="w-4 h-4 mr-3" />
                              Dashboard
                            </Link>
                          </DropdownMenu.Item>
                          <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
                          <DropdownMenu.Item
                            onSelect={handleLogout}
                            className="flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md cursor-pointer"
                          >
                            <MdLogout className="w-4 h-4 mr-3" />
                            Sign Out
                          </DropdownMenu.Item>
                        </>
                      ) : (
                        <DropdownMenu.Item asChild>
                          <Link
                            to="/login"
                            className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md cursor-pointer"
                          >
                            <MdLogin className="w-4 h-4 mr-3" />
                            Sign In
                          </Link>
                        </DropdownMenu.Item>
                      )}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </Tooltip.Root>

              {/* Quick Bulk Order Button */}
              <Dialog.Root open={isBulkOrderOpen} onOpenChange={setIsBulkOrderOpen}>
                <Dialog.Trigger asChild>
                  <button
                    className="hidden lg:inline-flex items-center gap-2 bg-[#1B365D] text-white rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-[#152a48] hover:shadow-md"
                  >
                    <div className="relative flex h-5 w-5 items-center justify-center">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-30"></span>
                      <MdCloudUpload className="relative inline-flex w-5 h-5" />
                    </div>
                    <span>Quick Bulk Order</span>
                  </button>
                </Dialog.Trigger>
                <Dialog.Portal>
                  <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] transition-opacity" />
                  <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] sm:w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl z-[9999] overflow-y-auto outline-none">
                    <BulkOrderUpload isModal={true} onClose={() => setIsBulkOrderOpen(false)} />
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>

              {/* Wishlist Button */}
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Link
                    to="/account/wishlist"
                    onClick={handleWishlistClick}
                    className="relative p-2 text-[#1B365D] hover:text-secondary-600 rounded-lg transition-colors hidden lg:block"
                  >
                    <Heart className="w-5 h-5" strokeWidth={2} />
                  </Link>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className="bg-gray-900 text-white px-2 py-1 rounded text-xs"
                    sideOffset={5}
                  >
                    Wishlist
                    <Tooltip.Arrow className="fill-gray-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>

              {/* Cart Button */}
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Link
                    to="/cart"
                    className="relative flex items-center gap-2 px-2 py-2 text-[#1B365D] hover:text-secondary-600 rounded-lg transition-colors"
                  >
                    <div className="relative">
                      <ShoppingCart className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div className="flex-col text-left hidden sm:flex">
                      <span className="text-[10px] font-medium leading-tight text-gray-500">{itemCount} Items</span>
                      <span className="text-xs font-bold leading-tight">Rs. {totalValue.toLocaleString()}</span>
                    </div>
                  </Link>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className="bg-gray-900 text-white px-2 py-1 rounded text-xs"
                    sideOffset={5}
                  >
                    Shopping Cart {itemCount > 0 ? `(${itemCount})` : ''}
                    <Tooltip.Arrow className="fill-gray-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>

          {/* Mobile Menu Button */}
          <Dialog.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <Dialog.Trigger asChild>
              <button className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                {isMenuOpen ? (
                  <MdClose className="w-6 h-6" />
                ) : (
                  <MdMenu className="w-6 h-6" />
                )}
              </button>
            </Dialog.Trigger>

            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
              <Dialog.Content className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-xl z-50 overflow-y-auto">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-6">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Menu
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button className="p-2 hover:bg-gray-100 rounded-md transition-colors">
                        <MdClose className="w-5 h-5" />
                      </button>
                    </Dialog.Close>
                  </div>

                  <div className="space-y-4">
                    {/* Mobile Navigation Links */}
                    <div className="space-y-2">
                      <button
                        className="w-full flex items-center px-4 py-3 bg-[#1B365D] text-white rounded-lg transition-all duration-200 hover:bg-[#152a48] hover:shadow-md font-medium text-sm"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsBulkOrderOpen(true);
                        }}
                      >
                        <div className="relative flex h-5 w-5 items-center justify-center mr-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-30"></span>
                          <MdCloudUpload className="relative inline-flex w-5 h-5" />
                        </div>
                        <span className="flex-1 text-left">Quick Bulk Order</span>
                      </button>

                      <Link
                        to="/products"
                        className="flex items-center px-3 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <MdGridView className="w-5 h-5 mr-3" />
                        All Products
                      </Link>
                      <Link
                        to="/brands"
                        className="flex items-center px-3 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <MdGridView className="w-5 h-5 mr-3" />
                        Brands
                      </Link>
                    </div>

                    <Separator.Root className="h-px bg-gray-200" />

                    {/* Account Section */}
                    <div className="space-y-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start px-3 py-3 h-auto text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                          handleAccountClick();
                          setIsMenuOpen(false);
                        }}
                      >
                        <MdPerson className="w-5 h-5 mr-3" />
                        {currentUser ? "My Account" : "Login"}
                      </Button>

                      {currentUser && (
                        <Button
                          variant="ghost"
                          className="w-full justify-start px-3 py-3 h-auto text-gray-700 hover:bg-gray-100"
                          onClick={() => {
                            handleLogout();
                            setIsMenuOpen(false);
                          }}
                        >
                          <MdLogout className="w-5 h-5 mr-3" />
                          Logout
                        </Button>
                      )}

                      <Link
                        to="/account/wishlist"
                        onClick={(e) => {
                          handleWishlistClick(e);
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center px-3 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MdFavorite className="w-5 h-5 mr-3" />
                        Wishlist
                      </Link>

                      <Link
                        to="/cart"
                        className="flex items-center px-3 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="flex items-center">
                          <div className="relative mr-3">
                            <AiOutlineShoppingCart className="w-5 h-5" />
                            {itemCount > 0 && (
                              <span className="absolute -top-2 -right-2 bg-secondary-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                                {itemCount}
                              </span>
                            )}
                          </div>
                          Cart {itemCount > 0 ? `(${itemCount})` : ''}
                        </div>
                      </Link>
                    </div>

                    <Separator.Root className="h-px bg-gray-200" />

                    {/* Trust Badges */}
                    <div className="bg-[#F8F9FA] rounded-xl p-5 space-y-5 border border-gray-100 mx-2 mt-4">
                      <div className="flex items-center gap-4">
                        <Package className="w-6 h-6 text-gray-800" strokeWidth={1.5} />
                        <div className="flex flex-col">
                          <span className="text-red-600 font-bold text-sm leading-tight">{stats?.productsCount !== undefined ? `${stats.productsCount}+` : "20,000+"}</span>
                          <span className="text-gray-800 text-xs font-medium leading-tight">Products</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <Award className="w-6 h-6 text-gray-800" strokeWidth={1.5} />
                        <div className="flex flex-col">
                          <span className="text-red-600 font-bold text-sm leading-tight">{stats?.brandsCount !== undefined ? `${stats.brandsCount}+` : "450+"}</span>
                          <span className="text-gray-800 text-xs font-medium leading-tight">Trusted Brands</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <CheckCircle2 className="w-6 h-6 text-gray-800" strokeWidth={1.5} />
                        <div className="flex flex-col">
                          <span className="text-red-600 font-bold text-sm leading-tight">100%</span>
                          <span className="text-gray-800 text-xs font-medium leading-tight">Original</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <ShieldCheck className="w-6 h-6 text-gray-800" strokeWidth={1.5} />
                        <div className="flex flex-col">
                          <span className="text-red-600 font-bold text-sm leading-tight">Assured</span>
                          <span className="text-gray-800 text-xs font-medium leading-tight">Best Prices</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>

      {/* Categories Menu - Hidden in account routes */}
      {!isAccountRoute && (() => {
        // A category has sub-categories if its children array is non-empty
        const hasSubcategories = (category) => {
          return Array.isArray(category?.children) && category.children.length > 0;
        };

        return (
          <div className="bg-primary-100 h-9 overflow-x-auto lg:overflow-visible hide-scrollbar">
            <div className="w-full lg:container mx-auto px-2 sm:px-4 h-full min-w-max relative">
              <NavigationMenu.Root className="h-full flex items-center justify-start lg:justify-center">
                <NavigationMenu.List
                  className="flex items-center h-full gap-4 lg:gap-8 justify-start lg:justify-center px-2 whitespace-nowrap static"
                >
                  {/* Category Dropdown */}
                  <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full static">
                    <div
                      onMouseEnter={handleMouseEnterDropdown}
                      onMouseLeave={handleMouseLeaveDropdown}
                      className="h-full flex items-center relative"
                    >
                      <button className="flex items-center px-2 py-1 text-[#1B365D] hover:bg-primary-300 hover:text-secondary-600 focus:outline-none gap-2 rounded-md transition-colors font-semibold text-sm">
                        <MdGridView className="w-4 h-4" />
                        Category
                      </button>

                      {isDropdownOpen && (
                        <div
                          className="fixed md:absolute z-header top-[84px] md:top-full left-2 md:left-0 w-[280px] sm:w-72 md:w-auto md:min-w-max bg-white rounded-lg shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-gray-100 overflow-hidden md:max-w-[90vw] mt-2 md:mt-2"
                          style={{ maxHeight: "calc(100vh - 92px)" }}
                          onMouseEnter={handleMouseEnterDropdown}
                          onMouseLeave={handleMouseLeaveDropdown}
                        >
                          <div className="md:flex md:flex-nowrap md:overflow-visible w-full min-h-[200px]">
                            {/* Mobile View - Single Level Navigation */}
                            <div className="md:hidden w-full">
                              {/* Current Level Display */}
                              <div className="p-3 bg-gray-50 border-b border-gray-100 flex items-center min-h-[3rem]">
                                {selectedLevelIndex > 0 && (
                                  <button
                                    onClick={handleBackClick}
                                    className="mr-2 p-1 text-gray-600 hover:bg-gray-200 rounded-md transition-colors flex-shrink-0"
                                  >
                                    <MdKeyboardArrowLeft className="w-4 h-4" />
                                  </button>
                                )}
                                <span className="text-sm font-semibold text-gray-700 truncate">
                                  {selectedLevelIndex === 0
                                    ? "Main Categories"
                                    : selectedCategories[selectedLevelIndex - 1]?.name ||
                                    "Sub Categories"}
                                </span>
                              </div>

                              {/* Categories List for Current Level */}
                              <div className="py-2 max-h-[60vh] overflow-y-auto min-h-[150px]">
                                {getCategoriesForLevel(selectedLevelIndex).length > 0 ? (
                                  getCategoriesForLevel(selectedLevelIndex).map((category) => (
                                    <div
                                      key={category._id}
                                      className="group hover:bg-gray-50 transition-colors"
                                    >
                                      <div className="flex items-center px-4 py-3">
                                        <button
                                          className="flex-grow text-left text-sm text-gray-700 hover:text-primary-600 font-medium group-hover:text-primary-600 transition-colors min-w-0 pr-2"
                                          onClick={(e) => handleCategoryClick(category, selectedLevelIndex, e)}
                                        >
                                          <span className="block">{category.name}</span>
                                        </button>
                                        {hasSubcategories(category) && (
                                          <button
                                            onClick={(e) => handleExpandSubcategory(category, selectedLevelIndex, e)}
                                            className="ml-2 p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                                          >
                                            <MdKeyboardArrowRight className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="px-4 py-8 text-center text-gray-500">
                                    <MdGridView className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">Loading categories...</p>
                                  </div>
                                )}

                                {/* View All Categories - only in main categories */}
                                {selectedLevelIndex === 0 && getCategoriesForLevel(selectedLevelIndex).length > 0 && (
                                  <>
                                    <Separator.Root className="h-px bg-gray-200 mx-4 my-2" />
                                    <div className="px-4 py-3 hover:bg-gray-50">
                                      <Link
                                        to="/categories"
                                        className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                                        onClick={() => setIsDropdownOpen(false)}
                                      >
                                        <span>View All Categories</span>
                                        <MdKeyboardArrowRight className="w-4 h-4 ml-1" />
                                      </Link>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Desktop View - Multi-column Layout */}
                            <div className="hidden md:flex md:flex-nowrap md:overflow-visible min-h-[200px]">
                              {getLevels().length > 0 && getLevels().map((level) => {
                                const categoriesForLevel = getCategoriesForLevel(level);
                                if (!categoriesForLevel?.length) {
                                  return null;
                                }

                                return (
                                  <div
                                    key={level}
                                    className="min-w-[250px] max-w-[280px] border-r border-gray-100 last:border-r-0 flex-shrink-0 max-h-[80vh] overflow-y-auto bg-white"
                                  >
                                    {/* Level Header */}
                                    <div className="p-3 md:px-4 md:py-4 border-b border-gray-100 sticky top-0 z-10 bg-white">
                                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        {level === 0
                                          ? "Browse"
                                          : selectedCategories[level - 1]?.name}
                                      </span>
                                    </div>

                                    {/* Categories List */}
                                    <div className="py-2 px-2">
                                      {categoriesForLevel.map((category) => (
                                        <div
                                          key={category._id}
                                          className={`group hover:bg-slate-50 transition-colors rounded-md mb-1 ${selectedCategories[level]?._id === category._id
                                            ? "bg-slate-50 border-l-2 border-blue-600 rounded-l-none"
                                            : "border-l-2 border-transparent"
                                            }`}
                                          onMouseEnter={(e) => {
                                            if (hasSubcategories(category)) {
                                              handleExpandSubcategory(category, level, e);
                                            } else {
                                              setSelectedCategories((prev) => prev.slice(0, level));
                                              setSelectedLevelIndex(level);
                                            }
                                          }}
                                        >
                                          <div className="flex items-center px-3 py-2">
                                            <button
                                              className={`flex-grow text-left text-sm font-medium transition-colors ${selectedCategories[level]?._id === category._id ? "text-blue-700" : "text-slate-700 group-hover:text-blue-600"}`}
                                              onClick={(e) => handleCategoryClick(category, level, e)}
                                            >
                                              {category.name}
                                            </button>
                                            {hasSubcategories(category) && (
                                              <button
                                                onClick={(e) => handleExpandSubcategory(category, level, e)}
                                                className={`ml-2 p-1 rounded-md transition-colors ${selectedCategories[level]?._id === category._id ? "text-blue-700" : "text-slate-400 group-hover:text-blue-600 group-hover:bg-blue-50"}`}
                                              >
                                                <MdKeyboardArrowRight className="w-4 h-4" />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      ))}

                                      {/* View All Categories - only in main categories */}
                                      {level === 0 && (
                                        <>
                                          <Separator.Root className="h-px bg-gray-200 mx-2 my-2" />
                                          <div className="px-2 py-1">
                                            <Link
                                              to="/categories"
                                              className="flex items-center justify-between text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors bg-blue-50 hover:bg-blue-100 rounded-md px-3 py-2"
                                              onClick={() => setIsDropdownOpen(false)}
                                            >
                                              <span>View All Categories</span>
                                              <MdKeyboardArrowRight className="w-4 h-4 ml-1" />
                                            </Link>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Fallback when no categories are loaded */}
                              {getLevels().length === 0 && (
                                <div className="min-w-[280px] bg-white p-8 text-center">
                                  <MdGridView className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                  <p className="text-sm text-gray-500">Loading categories...</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </NavigationMenu.Item>

                  {/* Other Navigation Links */}
                  <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/brands"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdLocalOffer className="w-4 h-4 mr-2" />
                        Brand
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>


                  <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/best-sellers"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdStars className="w-4 h-4 mr-2" />
                        Best Sellers
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>

                  <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/sales"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdSell className="w-4 h-4 mr-2" />
                        Sales
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>

                  <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/near-expiry"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdHourglassEmpty className="w-4 h-4 mr-2" />
                        Near Expiry
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>

                  {/* <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/membership"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdCardMembership className="w-4 h-4 mr-2" />
                        Membership
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item> */}

                  <NavigationMenu.Item className="flex-shrink-0">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/events"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <span className="relative mr-2">
                          <MdTimer className="w-4 h-4" />
                          {eventsCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-secondary-500 text-white rounded-full min-w-[16px] h-4 px-0.5 flex items-center justify-center text-[10px] font-bold leading-none">
                              {eventsCount > 99 ? '99+' : eventsCount}
                            </span>
                          )}
                        </span>
                        Events
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>

                  <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/blog"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdArticle className="w-4 h-4 mr-2" />
                        Blogs
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>

                  <NavigationMenu.Item className="flex-shrink-0 flex items-center h-full">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/about"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdPerson className="w-4 h-4 mr-2" />
                        About Us
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>

                  <NavigationMenu.Item className="flex-shrink-0">
                    <NavigationMenu.Link asChild>
                      <Link
                        to="/clinic-setup"
                        className="flex items-center px-2 py-1 text-sm text-[#1B365D] font-semibold hover:bg-primary-300 hover:text-secondary-600 transition-colors duration-200 rounded-md whitespace-nowrap"
                      >
                        <MdLocalHospital className="w-4 h-4 mr-2" />
                        New Clinic Setup
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>

                </NavigationMenu.List>
              </NavigationMenu.Root>
            </div>
          </div>
        );
      })()}

    </header>
  );
};

export default Header;
